"use client";

/**
 * Single tile inside the indexed-card media strip.
 *
 * Extracted from `AdsAssistantPostMediaStrip.tsx` as Slice 17f-D pre-split work.
 * Image / carousel posts render the lightbox-opening button branch; video posts
 * (Slice 17f-D) render the poster + play overlay + Video badge branch with an
 * optional link-out anchor.
 */

import { Play } from "lucide-react";

const TILE_FRAME_CLASSES =
  "group relative overflow-hidden rounded-lg border border-gray-200 ring-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:border-zinc-600";

const REMOVE_BUTTON_CLASSES =
  "absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-rose-600 text-[11px] font-bold leading-none text-white shadow " +
  "hover:bg-rose-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 disabled:opacity-50";

type RemoveButtonProps = {
  onClick: () => void;
  label: string;
  disabled?: boolean;
  /** Truthy → render `…` instead of `×` while a remove call is in-flight. */
  pending?: boolean;
};

type VideoConfig = {
  /** External link-out URL (FB post URL etc.) or null to render as static tile. */
  externalLink: string | null;
  /** aria-label override for the link-out anchor / static tile. */
  ariaLabel?: string;
};

type Props = {
  /** Signed (or stable) URL for the thumbnail; when null, image branch renders an expired-link placeholder and the video branch renders a neutral "Video" tile. */
  thumbnailUrl: string | null;
  /** aria-label on the main button (image / carousel branch). */
  ariaLabel: string;
  /** Click handler (typically opens the lightbox). Ignored when `video` is set. */
  onClick?: () => void;
  /** Disables the main button (e.g. busy state). The button is also disabled automatically when `thumbnailUrl` is null. Ignored when `video` is set. */
  disabled?: boolean;
  /** Forwarded to the `<img>` so the caller can refresh signed URLs on first error. */
  onImageError?: () => void;
  /** Optional inline `×` overlay (ingest mode). Works in both image and video branches. */
  removeButton?: RemoveButtonProps;
  /** When > 0, renders a `+N` badge in the bottom-right corner. Ignored when `video` is set. */
  extraCount?: number;
  /**
   * Slice 17f-D — when set, renders the tile as a video poster with a play
   * overlay and "Video" badge. When `externalLink` is set, wraps in an
   * `<a target=_blank>`; otherwise a static `<div>` with no click target.
   */
  video?: VideoConfig;
};

export function AdsAssistantPostMediaTile(props: Props) {
  const { thumbnailUrl, ariaLabel, onClick, disabled, onImageError, removeButton, extraCount, video } = props;
  if (video) {
    return (
      <div className="relative shrink-0">
        <VideoTileBody
          thumbnailUrl={thumbnailUrl}
          ariaLabel={video.ariaLabel ?? ariaLabel}
          externalLink={video.externalLink}
          onImageError={onImageError}
        />
        {removeButton ? <TileRemoveButton {...removeButton} /> : null}
      </div>
    );
  }
  const mainDisabled = !!disabled || !thumbnailUrl;
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        className={TILE_FRAME_CLASSES}
        aria-label={ariaLabel}
        disabled={mainDisabled}
        onClick={onClick}
      >
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt=""
            width={56}
            height={56}
            className="h-14 w-14 object-cover"
            loading="lazy"
            decoding="async"
            onError={() => onImageError?.()}
          />
        ) : (
          <div
            className="flex h-14 w-14 items-center justify-center bg-gray-100 text-[9px] text-gray-500 dark:bg-zinc-800 dark:text-gray-400"
            title="Preview link expired — refresh list to see thumbnail"
          >
            …
          </div>
        )}
        {extraCount && extraCount > 0 ? (
          <span className="absolute bottom-0.5 right-0.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold text-white">
            +{extraCount}
          </span>
        ) : null}
      </button>
      {removeButton ? <TileRemoveButton {...removeButton} /> : null}
    </div>
  );
}

function TileRemoveButton(props: RemoveButtonProps) {
  return (
    <button
      type="button"
      className={REMOVE_BUTTON_CLASSES}
      aria-label={props.label}
      disabled={props.disabled}
      onClick={props.onClick}
    >
      {props.pending ? "…" : "×"}
    </button>
  );
}

function VideoPosterContent({
  thumbnailUrl,
  onImageError,
}: {
  thumbnailUrl: string | null;
  onImageError?: () => void;
}) {
  return (
    <>
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt=""
          width={56}
          height={56}
          className="h-14 w-14 object-cover"
          loading="lazy"
          decoding="async"
          onError={() => onImageError?.()}
        />
      ) : (
        <div
          className="flex h-14 w-14 items-center justify-center bg-gray-200 text-[9px] font-medium uppercase tracking-wide text-gray-600 dark:bg-zinc-700 dark:text-gray-300"
          title="Video preview unavailable — open on platform to play."
        >
          Video
        </div>
      )}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white shadow ring-1 ring-white/40">
          <Play size={14} className="fill-current" aria-hidden="true" />
        </span>
      </span>
      <span className="pointer-events-none absolute bottom-0.5 left-0.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
        Video
      </span>
    </>
  );
}

function VideoTileBody({
  thumbnailUrl,
  ariaLabel,
  externalLink,
  onImageError,
}: {
  thumbnailUrl: string | null;
  ariaLabel: string;
  externalLink: string | null;
  onImageError?: () => void;
}) {
  if (externalLink) {
    return (
      <a
        href={externalLink}
        target="_blank"
        rel="noopener noreferrer"
        className={TILE_FRAME_CLASSES + " inline-block"}
        aria-label={ariaLabel}
      >
        <VideoPosterContent thumbnailUrl={thumbnailUrl} onImageError={onImageError} />
      </a>
    );
  }
  return (
    <div
      className={TILE_FRAME_CLASSES + " inline-block"}
      role="img"
      aria-label={ariaLabel}
    >
      <VideoPosterContent thumbnailUrl={thumbnailUrl} onImageError={onImageError} />
    </div>
  );
}
